import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ChatInterface from '../components/ChatInterface';
import ProjectPanel from '../components/ProjectPanel';
import PreviewPanel from '../components/PreviewPanel';
import DeployPanel from '../components/DeployPanel';
import TeamPanel from '../components/TeamPanel';
import VersionHistory from '../components/VersionHistory';
import AIMemoryPanel from '../components/AIMemoryPanel';
import FeaturedTemplates from '../components/FeaturedTemplates';
import { Sparkles, Rocket, Users, History, Brain } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeploy, setShowDeploy] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const { user } = useAuth();
  const location = useLocation();

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      const data = response.data || [];
      setProjects(data);

      const newProjectId = location.state?.newProjectId;
      if (newProjectId) {
        const newProject = data.find(p => p.id === newProjectId);
        if (newProject) {
          setSelectedProject(newProject);
          return;
        }
      }

      if (data.length > 0 && !selectedProject) {
        setSelectedProject(data[0]);
      } else if (selectedProject) {
        const updated = data.find(p => p.id === selectedProject.id);
        if (updated) setSelectedProject(updated);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, [location.state?.newProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  if (!loadingProjects && projects.length === 0) {
    return (
      <div className="h-screen flex bg-background">
        <Sidebar
          projects={[]}
          selectedProject={null}
          onSelectProject={() => {}}
          loadingProjects={false}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="text-center max-w-md mx-auto pt-16 pb-8" data-testid="empty-projects-state">
            <Sparkles className="w-16 h-16 text-blue-400/40 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-foreground mb-3">No projects yet</h2>
            <p className="text-muted-foreground mb-6">
              Create your first project or start from a template below.
            </p>
            <a
              href="/new-project"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold transition-all"
              data-testid="create-first-project-btn"
            >
              <Sparkles className="w-5 h-5" />
              Create Your First Project
            </a>
          </div>
          <div className="border-t border-border">
            <FeaturedTemplates />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background">
      <Sidebar
        projects={projects}
        selectedProject={selectedProject}
        onSelectProject={setSelectedProject}
        loadingProjects={loadingProjects}
        onProjectDeleted={fetchProjects}
      />

      <div className="flex-1 flex flex-col">
        {selectedProject ? (
          <>
            <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-foreground" data-testid="project-title">
                  {selectedProject.name}
                </h1>
                <p className="text-sm text-muted-foreground">{selectedProject.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="toggle-memory-btn"
                  onClick={() => { setShowMemory(!showMemory); if (!showMemory) { setShowHistory(false); setShowTeam(false); setShowDeploy(false); setShowPreview(false); } }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showMemory
                      ? 'bg-cyan-600 text-white'
                      : 'bg-secondary text-foreground hover:bg-accent'
                  }`}
                >
                  <Brain className="w-4 h-4" />
                  Memory
                </button>
                <button
                  data-testid="toggle-history-btn"
                  onClick={() => { setShowHistory(!showHistory); if (!showHistory) { setShowMemory(false); setShowTeam(false); setShowDeploy(false); setShowPreview(false); } }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showHistory
                      ? 'bg-amber-600 text-white'
                      : 'bg-secondary text-foreground hover:bg-accent'
                  }`}
                >
                  <History className="w-4 h-4" />
                  History
                </button>
                <button
                  data-testid="toggle-team-btn"
                  onClick={() => { setShowTeam(!showTeam); if (!showTeam) { setShowDeploy(false); setShowPreview(false); setShowHistory(false); setShowMemory(false); } }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showTeam
                      ? 'bg-violet-600 text-white'
                      : 'bg-secondary text-foreground hover:bg-accent'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Team
                </button>
                <button
                  data-testid="toggle-deploy-btn"
                  onClick={() => { setShowDeploy(!showDeploy); if (!showDeploy) { setShowPreview(false); setShowTeam(false); setShowHistory(false); setShowMemory(false); } }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showDeploy
                      ? 'bg-blue-600 text-white'
                      : 'bg-secondary text-foreground hover:bg-accent'
                  }`}
                >
                  <Rocket className="w-4 h-4" />
                  Deploy
                </button>
                <button
                  data-testid="toggle-preview-btn"
                  onClick={() => { setShowPreview(!showPreview); if (!showPreview) { setShowDeploy(false); setShowTeam(false); setShowHistory(false); setShowMemory(false); } }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    showPreview
                      ? 'bg-blue-600 text-white'
                      : 'bg-secondary text-foreground hover:bg-accent'
                  }`}
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 min-w-0">
                {selectedProject.status === 'building' || selectedProject.status === 'testing' ? (
                  <ChatInterface project={selectedProject} />
                ) : (
                  <ProjectPanel project={selectedProject} />
                )}
              </div>
              {showPreview && (
                <div className="w-[420px] shrink-0 border-l border-border overflow-y-auto">
                  <PreviewPanel project={selectedProject} />
                </div>
              )}
              {showDeploy && (
                <div className="w-[420px] shrink-0 border-l border-border overflow-y-auto">
                  <DeployPanel project={selectedProject} />
                </div>
              )}
              {showTeam && (
                <div className="w-[420px] shrink-0 border-l border-border overflow-y-auto">
                  <TeamPanel project={selectedProject} />
                </div>
              )}
              {showHistory && (
                <div className="w-[420px] shrink-0 border-l border-border overflow-y-auto">
                  <VersionHistory project={selectedProject} />
                </div>
              )}
              {showMemory && (
                <div className="w-[420px] shrink-0 border-l border-border overflow-y-auto">
                  <AIMemoryPanel project={selectedProject} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4" />
              <p className="text-lg">Select a project to get started</p>
            </div>
            <div className="border-t border-border">
              <FeaturedTemplates />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
